// src/components/common/Sidebar.js
import { Activity, BarChart3, FileText, LogOut, Settings, Users, X } from 'lucide-react';
import logo from '../../assets/logo.png';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ isOpen, onClose, activeTab, onTabChange }) => {
  const { user, logout } = useAuth();
  
  

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/' },
    { id: 'officers', label: 'Officers', icon: Users, path: '/officers' },
    { id: 'scan-history', label: 'Scan History', icon: Activity, path: '/scan-history' },
    { id: 'reports', label: 'Reports', icon: FileText, path: '/reports' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' }
  ];

  const handleTabChange = (tabId) => {
    onTabChange(tabId);
    onClose(); // Close sidebar on mobile after selection
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden transition-all duration-300" 
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-700/50 transform ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-out lg:translate-x-0 lg:static lg:z-auto`}>
        
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200/50 dark:border-gray-700/50">
          <div className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg bg-white flex items-center justify-center transition-transform duration-300 group-hover:scale-105 group-hover:rotate-3">
                <img src={logo} alt="InvisiGuard" className="w-9 h-9 object-contain" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900 animate-pulse-soft"></div>
            </div>
            <div>
              <h1 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                InvisiGuard
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">v2.1.0</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-8 px-4 space-y-2">
          {menuItems.map((item, index) => {
            const IconComponent = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                style={{ animationDelay: `${index * 50}ms` }}
                className={`group relative w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 animate-fade-in-up overflow-hidden ${
                  isActive
                    ? 'bg-gradient-to-r from-primary-500 to-accent-600 text-white shadow-lg shadow-primary-500/25'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:translate-x-0.5'
                }`}
              >
                <div className={`p-2 rounded-lg mr-3 transition-all duration-200 ${
                  isActive ? 'bg-white/20' : 'group-hover:bg-gray-200 dark:group-hover:bg-gray-700 group-hover:scale-110'
                }`}>
                  <IconComponent className="h-4 w-4" />
                </div>
                <span className="font-medium">{item.label}</span>
                {isActive && <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse-soft" />}
              </button>
            );
          })}
        </nav>

        {/* Quick Stats */}
        {/* <div className="mt-8 mx-4 p-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-xl border border-gray-200/50 dark:border-gray-600/50">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Quick Stats</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-400">Active Officers</span>
              <span className="font-medium text-emerald-600 dark:text-emerald-400">3/4</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-400">Today's Scans</span>
              <span className="font-medium text-primary-600 dark:text-primary-400">34</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-400">System Status</span>
              <span className="font-medium text-emerald-600 dark:text-emerald-400">Online</span>
            </div>
          </div>
        </div> */}

        {/* User Profile */}
        <div className="absolute bottom-6 left-4 right-4 animate-fade-in-up" style={{ animationDelay: '250ms' }}>
          <div className="p-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-xl border border-gray-200/50 dark:border-gray-600/50 hover-lift hover:shadow-soft">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-sm">{user?.avatar || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {user?.firstName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.role}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="group w-full flex items-center justify-center px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 text-sm font-medium hover:shadow-sm"
            >
              <LogOut className="h-4 w-4 mr-2 transition-transform duration-200 group-hover:-translate-x-0.5" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;